/**
 * filesystemSpec — NAS / file-share connectors (host + port + auth + ACL).
 *
 * v1 source-of-truth: ando-frontend/app/pages/FilesystemHubPage.tsx (1002)
 *
 * v1 page does the full host/port/ACL CRUD inside one big monolith. The
 * v2 wizard splits it into discrete steps:
 *   1. credentials   → host, port, share, auth-mode (SMB/NFS), user/pass
 *   2. form (acl)    → path prefix, allowed users (comma-separated)
 *   3. confirm
 *
 * Non-OAuth — `connect()` posts synchronously, no redirect. Wizard's
 * generic flow handles this via the empty `result.redirect_url` case.
 */

import type { BaseConnection, CapabilityWizardSpec } from "@ando/ui";
import { i18n as i18nSingleton } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { HardDrive, Lock, Network, Shield } from "lucide-react";

import { createCapabilityApi } from "../api/capabilityApi";

export interface FilesystemConnection extends BaseConnection {
  readonly host?: string;
  readonly share?: string;
  readonly auth_mode?: "smb" | "nfs";
}

const iconCls = "h-4 w-4";

export const filesystemSpec: CapabilityWizardSpec<FilesystemConnection> = {
  capability: "filesystem",
  title: msg`Cartelle e NAS`,
  // Desktop-only: indexes a local folder or an OS-mounted network share. The
  // host/share/SMB ``creds`` step below is VESTIGIAL — the real flow runs in
  // the custom ``FilesystemConnectDialog`` (native folder picker → scan → ACL),
  // routed via ``ConnectorDialogsLayer``. Copy here must describe THAT flow, not
  // the legacy web-bridge NAS connector, or the dialog header + catalog card +
  // benefits grid contradict each other (UX review P0-2).
  subtitle: msg`Indicizza una cartella del tuo computer o un'unità di rete (app desktop)`,
  connectNoun: "cartella",
  connectNounPlural: "cartelle",
  connectNounGender: "f",
  icon: <HardDrive className="h-5 w-5" aria-hidden />,
  gdprDeleteWindowMinutes: 30,

  vendors: [
    {
      id: "filesystem",
      label: msg`File system`,
      icon: <HardDrive className={iconCls} aria-hidden />,
    },
  ],

  steps: [
    {
      id: "creds",
      kind: "credentials",
      title: msg`Connessione`,
      description: msg`Host, share e credenziali della tua rete locale.`,
      fields: [
        {
          name: "host",
          label: "Host / IP",
          type: "string",
          required: true,
          placeholder: "nas.acme.local",
        },
        { name: "port", label: msg`Porta`, type: "number", placeholder: "445" },
        {
          name: "share",
          label: "Share / Volume",
          type: "string",
          required: true,
          placeholder: "documents",
        },
        {
          name: "auth_mode",
          label: msg`Protocollo`,
          type: "select",
          required: true,
          options: [
            { value: "smb", label: "SMB" },
            { value: "nfs", label: "NFS" },
          ],
        },
        { name: "username", label: msg`Utente`, type: "string" },
        { name: "password", label: "Password", type: "password" },
      ],
    },
    {
      id: "acl",
      kind: "form",
      title: "ACL",
      description: msg`Limita l'accesso a un sotto-percorso e a utenti specifici.`,
      fields: [
        {
          name: "path_prefix",
          label: msg`Percorso (prefisso)`,
          type: "string",
          placeholder: "/projects/2026",
        },
        {
          name: "allowed_users",
          label: msg`Utenti autorizzati`,
          type: "string",
          helperText: msg`Email separate da virgola — vuoto = solo te.`,
        },
      ],
    },
    { id: "confirm", kind: "confirm", title: msg`Conferma` },
  ],

  benefits: [
    {
      icon: <HardDrive className={iconCls} aria-hidden />,
      title: msg`Cartelle locali e NAS`,
      body: msg`Scegli una cartella del tuo computer o un'unità di rete montata.`,
    },
    {
      icon: <Network className={iconCls} aria-hidden />,
      title: msg`Resta sincronizzata`,
      body: msg`Nuovi file e modifiche vengono indicizzati in automatico.`,
    },
    {
      icon: <Lock className={iconCls} aria-hidden />,
      title: msg`Accesso controllato`,
      body: msg`Scegli con chi condividere l'accesso ai documenti.`,
    },
    {
      icon: <Shield className={iconCls} aria-hidden />,
      title: msg`App desktop`,
      body: msg`La scansione gira sul tuo computer — nessun agente da installare.`,
    },
  ],

  // ``api`` is VESTIGIAL for filesystem: the custom ``FilesystemConnectDialog``
  // owns connect (folder picker → fsSync), and ``listConnectionsForCapability``
  // routes the connection list to ``GET /filesystem/connectors`` (see
  // enterpriseConnections.ts). Kept to satisfy the spec contract.
  api: createCapabilityApi<FilesystemConnection>({
    capability: "filesystem",
    defaultVendor: "filesystem",
  }),
  connectionKeyField: "id" as keyof FilesystemConnection,
  // Local-folder connections have no host/share — the connected-row display
  // name comes from ``ConnectionSummary.connection_key`` (folder basename) set
  // by the desktop create. This fallback only fires for the rare raw spec use.
  getDisplayName: (c) =>
    c.host && c.share ? `${c.host}/${c.share}` : (c.id ?? i18nSingleton._(msg`Cartella`)),
  getSecondaryId: (c) => (c.auth_mode ? c.auth_mode.toUpperCase() : null),
};
